import './portal.css';
import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import ParentAppSidebar from './ParentAppSidebar';
import { apiRequest } from '@/utils/api-request';
import { ParentAppContext, SchoolStatus } from './ParentAppContext';
import { isErpHost, erpPath } from '@/utils/erp-host';

export default function ParentAppLayout() {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [isOnboarded, setIsOnboarded] = useState<boolean | null>(null);
  const [schoolData, setSchoolData] = useState<SchoolStatus | null>(null);
  const [isMaster, setIsMaster] = useState(false);

  useEffect(() => {
    // ERP-login staff authenticate via a pa-token (erp_auth_token cookie) and
    // have no crm_user in localStorage. Their authorization + school context is
    // resolved server-side by /school/status, so treat the token as authorized
    // and let the status call provide onboarding/school data.
    if (Cookies.get('erp_auth_token')) {
      setIsAuthorized(true);
      checkOnboardingStatus();
      return;
    }

    // CRM-user path: authorize from the CRM session in localStorage.
    const userStr = localStorage.getItem('crm_user');
    const user = userStr ? JSON.parse(userStr) : null;
    const clientId = user?.data?.data?.client_id_fk || user?.data?.client_id_fk || user?.client_id_fk;
    const sdOrgId  = user?.data?.data?.sd_org_id || user?.data?.data?.client?.sd_org_id || user?.data?.sd_org_id || user?.sd_org_id || user?.client?.sd_org_id;
    const isMasterAccount = clientId == 9 || clientId === '9';
    setIsMaster(isMasterAccount);

    if (isMasterAccount || (sdOrgId && sdOrgId !== 0 && sdOrgId !== '0')) {
      setIsAuthorized(true);
      if (isMasterAccount) setIsOnboarded(true);
      else checkOnboardingStatus();
    } else {
      setIsAuthorized(false);
    }
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const response = await apiRequest('/api/parent-app/admin/school/status', { method: 'GET' });
      if (response.success) {
        setIsOnboarded(response.data.isOnboarded);
        if (response.data.isOnboarded && response.data.school) setSchoolData(response.data.school);
      }
    } catch {
      setIsOnboarded(false);
    }
  };

  const refreshStatus = async () => { await checkOnboardingStatus(); };

  /* ── Loading ── */
  if (isAuthorized === null || (isAuthorized && !isMaster && isOnboarded === null)) {
    return (
      <div className="portal-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            border: '3px solid var(--accent)', borderTopColor: 'transparent',
            animation: 'spin 0.7s linear infinite', margin: '0 auto 14px',
          }} />
          <p style={{ color: 'var(--text-2)', fontSize: 14, fontWeight: 600 }}>Loading…</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  /* ── Unauthorized ── */
  if (!isAuthorized) {
    return (
      <div className="portal-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 24px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20,
            background: 'var(--danger-wash)', color: 'var(--danger)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', fontSize: 28,
          }}>🔒</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>Access Denied</h2>
          <p style={{ margin: '0 0 24px', color: 'var(--text-2)', fontSize: 14, lineHeight: 1.6 }}>
            Parent App Portal is only available for school administrators.
          </p>
          <button className="btn btn-primary" onClick={() => navigate(isErpHost() ? erpPath('login') : '/dashboard')}>
            {isErpHost() ? 'Back to Login' : 'Go to Dashboard'}
          </button>
        </div>
      </div>
    );
  }

  /* ── Not onboarded ── */
  if (!isOnboarded && !isMaster) {
    return (
      <div className="portal-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: '0 24px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20,
            background: 'var(--accent-wash)', color: 'var(--accent-deep)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', fontSize: 28,
          }}>📱</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>Not set up yet</h2>
          <p style={{ margin: '0 0 24px', color: 'var(--text-2)', fontSize: 14, lineHeight: 1.6 }}>
            Your school isn't on Parent App yet. Contact your YellowSlate account manager to get started.
          </p>
          <a href="mailto:support@yellowslate.com" className="btn btn-primary">Contact YellowSlate</a>
        </div>
      </div>
    );
  }

  /* ── Main shell ── */
  return (
    <ParentAppContext.Provider value={{ school: schoolData, refreshStatus }}>
      <div className="portal-root">
        <ParentAppSidebar />
        <div className="main">
          <Outlet />
        </div>
      </div>
    </ParentAppContext.Provider>
  );
}
